/**
 * The calls a supervisor is listening in on from their own phone (ADR-029), kept by this tab.
 *
 * Starting one answers with the monitor's id, which is the channel uuid the ACD gives the
 * supervisor's leg, and so the id of the call their phone is about to ring with. The phone needs two
 * things from it: to answer that call without a click, since the supervisor has just asked for it,
 * and to show the listen, whisper and barge controls on it. The floor that starts a monitor and the
 * phone that shows it are different parts of the page, so this is module state with subscribers, as
 * `rememberConsult` keeps a consult. Pure apart from that, for `test/monitor.test.ts`.
 */

export type MonitorMode = "listen" | "whisper" | "barge";

export interface Monitoring {
  monitorId: string;
  /** The rep whose call it is, for the phone to say whose call is being listened to. */
  repName: string;
  /** The caller's leg of the call listened to, as the floor names a rep's call; null if unknown. */
  callId: string | null;
  /** What the switch was last asked for. A monitor starts listening. */
  mode: MonitorMode;
}

/** The three ways to take part, in the supervisor's words and in the order the phone shows them. */
export const MONITOR_MODES: readonly { mode: MonitorMode; label: string; hint: string }[] = [
  { mode: "listen", label: "Listen", hint: "You hear everyone on the call, and nobody hears you." },
  { mode: "whisper", label: "Whisper", hint: "The reps hear you. The caller does not." },
  { mode: "barge", label: "Barge in", hint: "Everyone on the call hears you." },
];

let monitors: ReadonlyMap<string, Monitoring> = new Map();
const watchers = new Set<() => void>();

function publish(next: ReadonlyMap<string, Monitoring>): void {
  monitors = next;
  notifyAll();
}

function notifyAll(): void {
  for (const notify of watchers) notify();
}

/** Listen-ins asked for whose id the server has not answered with yet. */
let asking = 0;

/** The longest a request for a listen-in keeps the phone quiet without an answer. */
export const ASKING_MS = 5_000;

/**
 * A listen-in is being asked for. Its call is known by the id the server answers with, but the
 * switch's invite can reach the phone first: until that answer is in, a call ringing here is most
 * likely the listen-in, and it must not ring either (`toneFor`). Returns what says the answer is
 * in, after `rememberMonitor`; it also runs by itself after `ASKING_MS`, so a request whose answer
 * never reaches this page cannot keep the phone quiet for longer than that.
 */
export function askingToMonitor(): () => void {
  asking += 1;
  notifyAll();
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    asking -= 1;
    notifyAll();
  };
  const timer = setTimeout(settle, ASKING_MS);
  return settle;
}

/** Whether a listen-in has been asked for and the server has not answered yet. */
export function monitorAsked(): boolean {
  return asking > 0;
}

export function rememberMonitor(monitorId: string, repName: string, callId: string | null = null): void {
  const next = new Map(monitors);
  next.set(monitorId, { monitorId, repName, callId, mode: "listen" });
  publish(next);
}

/**
 * The supervisor's listen-ins on the phone right now, ringing or up: what the floor hangs up before
 * starting another, and whose calls it shows as already being listened to. A monitor whose call has
 * not rung yet, or has gone, is not one.
 */
export function listenInsOnPhone(
  known: ReadonlyMap<string, Monitoring>,
  calls: readonly { id: string }[],
): Monitoring[] {
  const live = new Set(calls.map((c) => c.id));
  return [...known.values()].filter((m) => live.has(m.monitorId));
}

export function setMonitorMode(monitorId: string, mode: MonitorMode): void {
  const current = monitors.get(monitorId);
  if (!current || current.mode === mode) return;
  const next = new Map(monitors);
  next.set(monitorId, { ...current, mode });
  publish(next);
}

export function forgetMonitor(monitorId: string): void {
  if (!monitors.has(monitorId)) return;
  const next = new Map(monitors);
  next.delete(monitorId);
  publish(next);
}

export function currentMonitors(): ReadonlyMap<string, Monitoring> {
  return monitors;
}

export function subscribeMonitors(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

/** What the phone does by itself with a listen-in that rings: answer it, after hanging up `end`. */
export interface MonitorAnswer {
  callId: string;
  /** The supervisor's earlier listen-ins still on the phone. */
  end: string[];
}

/**
 * The listen-in the phone answers by itself, or null: the supervisor asked for it a moment ago on
 * the floor, so it is answered whether this browser's own auto-answer is on or off (and it never
 * rings, `toneFor`). The newest one, when two ring at once.
 *
 * Anything else on the phone decides how:
 * - Nothing: answered. Unless this browser's own auto-answer is on (`browserAnswers`), which
 *   answers the call the moment it rings; answering it here as well sent the switch a second
 *   answer from a second peer connection, and the supervisor heard silence (2026-09-24).
 * - Only earlier listen-ins: they are hung up (`end`), then this one is answered. A supervisor
 *   moving from one rep's call to the next listens to one call at a time. Hung up rather than held:
 *   holding a leg that sits in a call's room is the room's business, not the supervisor's phone's.
 * - Any other call, a client's, a colleague's or their own room: null. Verto does not hold the
 *   first call when a second is answered, and putting a client on hold is the supervisor's
 *   decision, so the call waits for Hold & answer, as every other second call does.
 */
export function monitorToAnswer(
  calls: readonly { id: string; direction: string; state: string }[],
  known: ReadonlyMap<string, Monitoring>,
  browserAnswers: boolean,
): MonitorAnswer | null {
  const target = calls.findLast(
    (c) => known.has(c.id) && c.direction === "inbound" && c.state === "ringing",
  );
  if (!target) return null;
  const others = calls.filter((c) => c.id !== target.id);
  if (others.some((c) => !known.has(c.id))) return null;
  if (others.length === 0 && browserAnswers) return null;
  return { callId: target.id, end: others.map((c) => c.id) };
}

/**
 * Whether `callId` is a listen-in the phone is joining by itself (`monitorToAnswer`), so it shows
 * "Connecting to call session" rather than Answer and Decline, which it flashed for the moment
 * before the answer went through (2026-09-25). While a Listen is still being asked for, the phone's
 * only ringing call counts too: the switch's invite can beat the answer that names it.
 *
 * A listen-in that rings over another call is not one: it waits for Hold & answer, and needs its
 * buttons.
 */
export function joiningListenIn(
  calls: readonly { id: string; direction: string; state: string }[],
  known: ReadonlyMap<string, Monitoring>,
  callId: string,
  asking: boolean,
): boolean {
  const call = calls.find((c) => c.id === callId);
  if (!call || call.direction !== "inbound" || call.state !== "ringing") return false;
  if (monitorToAnswer(calls, known, false)?.callId === callId) return true;
  return asking && calls.length === 1 && known.size === 0;
}

/**
 * The monitors whose call has come and gone: rung, seen on the phone, and not there any more. `seen`
 * is the caller's memory of which monitors' calls it has seen, and is updated here.
 */
export function endedMonitors(
  calls: readonly { id: string }[],
  known: ReadonlyMap<string, Monitoring>,
  seen: Set<string>,
): string[] {
  const live = new Set(calls.map((c) => c.id));
  const ended: string[] = [];
  for (const id of known.keys()) {
    if (live.has(id)) seen.add(id);
    else if (seen.has(id)) {
      seen.delete(id);
      ended.push(id);
    }
  }
  return ended;
}
